import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { extractApiErrorMessage } from '@/lib/api-client';

import { adjustableParentsKey } from '../api/use-adjustable-parents';
import { allocatedDownpaymentKey } from '../api/use-allocated-downpayment';
import { appliedProvisionalKey } from '../api/use-applied-provisional';
import type { InvoiceDirection } from '../api/use-applied-provisional';
import { blOrderInvoicesKey } from '../api/use-bl-order-invoices';
import { coreValuesKey } from '../api/use-core-values';
import type { InvoiceRow } from '../types';
import { buildAdjustmentNoteSavePayload } from './adjustment-note-payload';
import type { AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import { saveInvoiceWithPdf } from './save-invoice-with-pdf';
import { useInvoiceCreatePdf } from './invoice-create-pdf-context';

export interface UseSaveAdjustmentMutationArgs {
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly noteType: 'Credit' | 'Debit';
  readonly existingInvoice: InvoiceRow | null;
  readonly onSaved: () => void;
  readonly onOpenChange: (open: boolean) => void;
  readonly aiExtractionId?: string;
}

export function useSaveAdjustmentMutation(args: UseSaveAdjustmentMutationArgs) {
  const qc = useQueryClient();
  const { file: pdfFile } = useInvoiceCreatePdf();
  const direction: InvoiceDirection = args.side === 'buy' ? 'Purchase' : 'Sale';
  const label = `${args.noteType} Note`;
  return useMutation<unknown, Error, AdjustmentNoteSubmitValues>({
    mutationFn: (values) =>
      saveInvoiceWithPdf(
        args.existingInvoice,
        buildAdjustmentNoteSavePayload(values, {
          side: args.side,
          orderId: args.orderId,
          blOrderId: args.blOrderId,
          noteType: args.noteType,
          existingInvoice: args.existingInvoice,
          ...(args.aiExtractionId !== undefined ? { aiExtractionId: args.aiExtractionId } : {}),
        }),
        pdfFile,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(args.blOrderId) });
      void qc.invalidateQueries({ queryKey: adjustableParentsKey(args.blOrderId, direction) });
      void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(args.blOrderId) });
      void qc.invalidateQueries({ queryKey: appliedProvisionalKey(args.blOrderId, direction) });
      // Notes append an expectation snapshot to the value log — refresh the grid live.
      void qc.invalidateQueries({ queryKey: coreValuesKey(args.blOrderId) });
      void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
      toast.success(args.existingInvoice !== null ? `${label} updated.` : `${label} created.`);
      args.onSaved();
      args.onOpenChange(false);
    },
    onError: (err) => {
      toast.error(extractApiErrorMessage(err, `Failed to save ${label}.`));
    },
  });
}
